import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Skeleton,
} from "@attmos/flowstack-ui";
import { ArrowRight } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import { useCanManageProjects, useCreateProject, useProjects } from "@/api/projects";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";

/**
 * The org's projects (fs#1207: real records, no longer one synthesized
 * project). An org owner also gets the New project form; everyone else sees
 * the list only — the server refuses their writes regardless.
 */
export function ProjectListPage(): JSX.Element {
  const { data: projects, isLoading, isError, error } = useProjects();
  const canManage = useCanManageProjects();
  const actions = canManage ? <NewProjectForm /> : undefined;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Projects" description="Loading…" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Projects" />
        <EmptyState
          title="Could not load projects."
          description={error instanceof Error ? error.message : "Unknown error."}
        />
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Projects" actions={actions} />
        <EmptyState
          title="No projects yet."
          description={
            canManage
              ? "Create your organization's first project to start submitting runs."
              : "Ask an owner of your organization to create a project."
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Projects"
        description={
          projects.length === 1 ? "1 project." : `${projects.length} projects in your organization.`
        }
        actions={actions}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((project) => (
          <Link
            key={project.project_id}
            to={`/projects/${project.slug}`}
            className="group block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Card
              elevation="raised"
              className="h-full transition-colors group-hover:border-cyan/40 group-hover:bg-surface-2"
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="font-display text-lg">{project.display_name}</CardTitle>
                  <ArrowRight
                    className="h-4 w-4 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:text-cyan group-hover:opacity-100"
                    aria-hidden
                  />
                </div>
                <CardDescription className="font-mono text-xs">{project.slug}</CardDescription>
              </CardHeader>
              <CardContent>
                {project.description ? (
                  <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                    {project.description}
                  </p>
                ) : null}
                <dl className="grid grid-cols-3 gap-3 text-xs">
                  <Stat label="Runs" value={project.run_count} />
                  <Stat label="Active" value={project.active_run_count} />
                  <Stat label="Artifacts" value={project.artifact_count} />
                </dl>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Name a new project; on success, open it. The server's refusal (a name
 * the org already has, a blank one) is shown under the field. */
function NewProjectForm(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateProject();
  const [name, setName] = React.useState("");

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!name.trim()) return;
    create.mutate(name, {
      onSuccess: (project) => navigate(`/projects/${project.slug}`),
    });
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-1" aria-label="New project">
      <div className="flex items-center gap-2">
        <Input
          aria-label="Project name"
          placeholder="Project name"
          value={name}
          maxLength={120}
          onChange={(event) => setName(event.target.value)}
          className="w-56"
        />
        <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
          New project
        </Button>
      </div>
      {create.isError ? (
        <p role="alert" className="text-xs text-destructive">
          {create.error instanceof Error ? create.error.message : "Could not create the project."}
        </p>
      ) : null}
    </form>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div>
      <dt className="label-micro text-muted-foreground">{label}</dt>
      <dd className="font-display text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
